import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ArrowDown, CalendarClock, CheckCircle2, ClipboardList, Paperclip } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { AttachmentChips } from './Attachments';

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) : '');

/**
 * Blocking shift briefing. Every operator reads and acknowledges the standing
 * pass-on notes once per shift before they can log anything. The acknowledgement
 * is recorded per operator per shift instance, so an overnight operator crossing
 * midnight is not asked twice.
 */
export function PassOnGate({ children }) {
  const [state, setState] = useState({ loading: true, notes: [], shift: null });
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  // The operator must reach the bottom of the briefing before the confirmation
  // unlocks — no acknowledging a wall of text you never scrolled through.
  const [readToEnd, setReadToEnd] = useState(false);
  // Attachments are part of the briefing, not a footnote: every file has to be
  // opened before the acknowledgement unlocks.
  const [openedIds, setOpenedIds] = useState(() => new Set());
  const scrollRef = useRef(null);

  const requiredAttachments = state.notes.flatMap((n) => n.attachments || []);
  const unopened = requiredAttachments.filter((a) => !openedIds.has(a.id));
  const markOpened = (a) => setOpenedIds((prev) => new Set(prev).add(a.id));
  const canConfirm = readToEnd && unopened.length === 0;

  useEffect(() => {
    api.get('/gsoc/pass-on/pending')
      .then(({ data }) => setState({ loading: false, notes: data.notes || [], shift: data }))
      .catch(() => setState({ loading: false, notes: [], shift: null }));
  }, []);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Short briefings that fit without a scrollbar count as read on sight.
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
    if (atEnd) setReadToEnd(true);
  }, []);

  // Re-evaluate once the notes have painted (a one-note briefing may not scroll).
  useEffect(() => {
    if (state.notes.length) requestAnimationFrame(checkScroll);
  }, [state.notes, checkScroll]);

  const jumpToEnd = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const acknowledge = async () => {
    setSaving(true);
    try {
      await api.post('/gsoc/pass-on/acknowledge', { note_ids: state.notes.map((n) => n.id) });
      toast.success('Shift briefing acknowledged');
      setState((s) => ({ ...s, notes: [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record your acknowledgement');
    } finally { setSaving(false); }
  };

  if (state.loading || state.notes.length === 0) return children;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
           style={{ backgroundColor: 'hsl(var(--bg-overlay) / 0.85)' }}
           data-testid="passon-gate-overlay">
        <div className="w-full max-w-3xl rounded-lg border border-line bg-surface shadow-2xl">
          <div className="p-6 border-b border-line flex items-start gap-3">
            <ClipboardList className="w-7 h-7 text-warning shrink-0" aria-hidden="true" />
            <div>
              <h2 className="text-lg md:text-lg font-semibold text-fg">Shift Briefing — read before you begin</h2>
              <p className="text-sm text-fg-muted mt-1">
                {state.notes.length} pass-on {state.notes.length === 1 ? 'note' : 'notes'} for the{' '}
                <strong className="text-fg">{state.shift?.shift_label}</strong> shift on {fmtDate(state.shift?.shift_date)}.
                You acknowledge these once per shift.
              </p>
            </div>
          </div>

          <div ref={scrollRef} onScroll={checkScroll}
               className="p-6 space-y-4 max-h-[55vh] overflow-y-auto relative" data-testid="passon-gate-notes">
            {state.notes.map((n) => (
              <div key={n.id}
                   className={`rounded-md border p-4 ${n.is_urgent
                     ? 'border-danger-border bg-danger-bg ring-1 ring-[hsl(var(--danger))]'
                     : 'border-line bg-surface-sunken'}`}
                   data-testid={`passon-note-${n.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className={`font-semibold flex items-center gap-2 ${n.is_urgent ? 'text-danger' : 'text-fg'}`}>
                    {n.is_urgent && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--danger))] text-white"
                            data-testid={`passon-note-urgent-${n.id}`}>
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />urgent
                      </span>
                    )}
                    {n.title}
                  </p>
                  <p className="text-xs text-fg-muted">{n.client_name} · {n.site_name}</p>
                </div>
                <p className="text-sm text-fg-secondary whitespace-pre-wrap mt-2">{n.body}</p>
                {(n.work_order_number || n.account_number) && (
                  <p className="text-xs text-fg-muted mt-2 flex flex-wrap gap-x-4"
                     data-testid={`passon-note-refs-${n.id}`}>
                    {n.work_order_number && <span>Work Order # <span className="text-fg font-medium">{n.work_order_number}</span></span>}
                    {n.account_number && <span>Account # <span className="text-fg font-medium">{n.account_number}</span></span>}
                  </p>
                )}
                {n.attachments?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wide text-fg-muted mb-1">Attached — open each file before you acknowledge</p>
                    <AttachmentChips items={n.attachments} testIdPrefix={`passon-gate-att-${n.id}`}
                                     openedIds={openedIds} onOpened={markOpened} />
                  </div>
                )}
                <p className="text-xs text-fg-muted mt-3">
                  Posted by {n.created_by_name} · {fmtStamp(n.created_at)}
                  {n.expires_at && <> · <CalendarClock className="w-3 h-3 inline" aria-hidden="true" /> expires {fmtDate(n.expires_at)}</>}
                </p>
              </div>
            ))}
          </div>

          <div className="p-6 border-t border-line space-y-3">
            {!readToEnd && (
              <button type="button" onClick={jumpToEnd} data-testid="passon-scroll-prompt"
                      className="w-full flex items-center justify-center gap-2 rounded-md border border-warning-border bg-warning-bg
                                 px-4 py-3 text-sm font-medium text-fg hover:opacity-90 transition-opacity">
                <ArrowDown className="w-4 h-4 animate-bounce" aria-hidden="true" />
                Scroll to the end of the briefing to continue
              </button>
            )}
            {readToEnd && unopened.length > 0 && (
              <div className="w-full flex items-start gap-2 rounded-md border border-warning-border bg-warning-bg
                              px-4 py-3 text-sm font-medium text-fg" data-testid="passon-attachment-prompt">
                <Paperclip className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  Open {unopened.length === 1 ? 'the attached file' : `all ${requiredAttachments.length} attached files`} before you
                  acknowledge — {requiredAttachments.length - unopened.length} of {requiredAttachments.length} opened.
                  <span className="block text-xs font-normal text-fg-secondary mt-0.5">
                    Still to open: {unopened.map((a) => a.filename).join(', ')}
                  </span>
                </span>
              </div>
            )}
            <label className={`flex items-start gap-3 text-sm ${canConfirm ? 'text-fg cursor-pointer' : 'text-fg-muted cursor-not-allowed'}`}>
              <input type="checkbox" checked={confirmed} disabled={!canConfirm}
                     onChange={(e) => setConfirmed(e.target.checked)}
                     className="mt-0.5 w-5 h-5 accent-[hsl(var(--primary))] disabled:cursor-not-allowed"
                     data-testid="passon-confirm-checkbox" />
              <span>
                I have read the pass-on information above
                {requiredAttachments.length > 0 && ', opened every attached file,'} and I am ready to take the shift.
              </span>
            </label>
            <Button onClick={acknowledge} disabled={!canConfirm || !confirmed || saving} className="w-full h-12 text-base"
                    data-testid="passon-acknowledge-btn">
              <CheckCircle2 className="w-5 h-5 mr-2" aria-hidden="true" />
              {saving ? 'Recording...' : 'Acknowledge & Continue'}
            </Button>
            <p className="text-xs text-fg-muted flex items-center gap-1 justify-center">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
              Your name, the shift and the time are recorded against each note.
            </p>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none select-none blur-sm opacity-40">{children}</div>
    </>
  );
}
